import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { InstalledEquipment } from './appointmentEquipment.models';
import {
  appointmentEquipmentReducer,
  AppointmentEquipmentState,
  initState,
} from './appointmentEquipment.reducer';

interface AppointmentEquipmentContextProps {
  equipment: InstalledEquipment[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchAppointmentEquipment: (
    locationId: string,
    options?: { signal?: AbortSignal }
  ) => Promise<void>;
}

const AppointmentEquipmentContext = createContext<
  AppointmentEquipmentContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AppointmentEquipmentState>;
}>;

export const AppointmentEquipmentProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(appointmentEquipmentReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const installedEquipment = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter((e) => e.locationId === state.selectedLocationId),
    [state.entities, state.ids, state.selectedLocationId]
  );

  const fetchAppointmentEquipment: AppointmentEquipmentContextProps['fetchAppointmentEquipment'] =
    useCallback(
      async (locationId, options) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return;
        }
        const params = new URLSearchParams({
          locationId,
        });

        dispatch({
          type: 'FETCH_INSTALLED_EQUIPMENT',
          payload: { locationId },
        });

        try {
          const installedEquipment = await httpClient.get<InstalledEquipment[]>(
            `/service-history/installed-equipment/?${params}`,
            { signal: options?.signal }
          );

          dispatch({
            type: 'FETCH_INSTALLED_EQUIPMENT_SUCCESS',
            payload: {
              installedEquipment,
            },
          });
        } catch (error) {
          if (error instanceof Error && error.message === 'canceled') {
            dispatch({ type: 'FETCH_INSTALLED_EQUIPMENT_CANCELLED' });
          } else {
            dispatch({
              type: 'FETCH_INSTALLED_EQUIPMENT_FAILURE',
              payload: { error: `${error}` },
            });
          }
        }
      },
      [httpClient, isOnline]
    );

  return (
    <AppointmentEquipmentContext.Provider
      value={{
        equipment: installedEquipment,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
        fetchAppointmentEquipment,
      }}
    >
      {children}
    </AppointmentEquipmentContext.Provider>
  );
};

export const useAppointmentEquipment = () => {
  const context = useContext(AppointmentEquipmentContext);
  if (context === undefined) {
    throw new Error(
      'useAppointmentEquipment must be used within an AppointmentEquipmentProvider'
    );
  }
  return context;
};
